/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import pkgInfo from '../../package.json';

const SENTRY_ENABLED = process.env.NODE_ENV === 'production'; // enable on deployed builds, not localhost

console.log(`SENTRY_ENABLED: ${SENTRY_ENABLED}`, {
  sha: import.meta.env.GITHUB_SHA,
});

const RATE = import.meta.env.MODE === 'production' ? 0.01 : 1.0; // FIXME: bump to 100% when we go live

export function initializeSentry() {
  Sentry.init({
    dsn: 'https://3b474b57b6176a3c2974b381355840d7@o279785.ingest.us.sentry.io/4509691732623360',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    debug: import.meta.env.MODE === 'development',
    release: import.meta.env.GITHUB_SHA || 'dev-build',
    enabled: SENTRY_ENABLED,
    environment: import.meta.env.MODE || 'development',
    sendDefaultPii: true,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: RATE, // production can consume ~1k transactions/day (limit is 10k/month)

    // Session Replay
    replaysSessionSampleRate: RATE, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  // set package version as a tag
  Sentry.setTag('version', pkgInfo.version);
}

/**
 * Enhanced logging function that logs to both console and Sentry
 * @param message - Log message
 * @param context - Optional context object with additional data
 * @param error - Optional Error object or unknown error data
 */
export const log = (
  message: string,
  context?: Record<string, unknown>,
  error?: Error | unknown
) => {
  // Always log to console for development debugging
  console.info(`[${message}`, context, error);

  // Send errors to Sentry
  if (error) {
    Sentry.withScope(scope => {
      scope.setTag('service', 'admin-portal');
      scope.setContext('log_context', context || {});

      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(message, 'error');
        scope.setExtra('error_details', error);
      }
    });
  } else {
    // For info logs, just send as breadcrumbs
    Sentry.addBreadcrumb({
      message,
      level: 'info',
      data: context,
    });
  }
};

export function logError(error: Error) {
  if (SENTRY_ENABLED) {
    Sentry.captureException(error);
  } else {
    console.error(error);
  }
}

export function setSentryUser(
  user: { email?: string; id?: string; displayName?: string } | null
) {
  if (SENTRY_ENABLED) {
    const sentryUser: Sentry.User | null = user
      ? {
          email: user.email || '',
          id: user.id || '',
          username: user.displayName || user.email || '',
        }
      : null;
    Sentry.setUser(sentryUser);
  }
}

export function breadcrumb(message: string, crumb?: any) {
  if (SENTRY_ENABLED) {
    Sentry.addBreadcrumb({
      category: 'admin-portal',
      message: message,
      data: crumb,
    });
  } else {
    console.info(`BREADCRUMB: ${message}:`, crumb);
  }
}
